import React, { useState, useEffect, createContext, useContext } from "react";
import notify from "devextreme/ui/notify";
import Server, { requestERPLYData } from "../api/Server";
import {
  getEmployees,
  getPointOfSales,
  getReasonCodes,
  getWarehouses,
  getStoreRegions,
} from "../redux/cash-in-out/cash-in-out-actions";
import { useDispatch } from "react-redux";
import { loadProgressBar } from "axios-progress-bar";
import {
  getBrands,
  getProductCategories,
  getProductGroups,
  getSuppliers,
  getCustomers,
  getCurrencies,
} from "../redux/general/actions";

const AuthContext = createContext({});

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginUrl, setLink] = useState("https://login.erply.com");
  const [activeLanguages, setActiveLanguages] = useState([]);
  const [configParams, setConfigParams] = useState({});
  const [orderedWarehouses, setOrderedWarehouses] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);

  const dispatch = useDispatch();

  const getCodes = (request) => dispatch(getReasonCodes(request));
  const fetchEmployees = (request) => dispatch(getEmployees(request));
  const fetchWarehouses = (request) => dispatch(getWarehouses(request));
  const fetchPointOfSales = (request) => dispatch(getPointOfSales(request));
  const fetchBrands = (request) => dispatch(getBrands(request));
  const fetchCategories = (request) => dispatch(getProductCategories(request));
  const fetchGroups = (request) => dispatch(getProductGroups(request));
  const fetchSuppliers = (request) => dispatch(getSuppliers(request));
  const fetchStoreRegions = (request) => dispatch(getStoreRegions(request));
  const fetchCustomers = (request) => dispatch(getCustomers(request));
  const fetchCurrency = (request) => dispatch(getCurrencies(request));

  useEffect(() => {
    let urlSearchParams = new URLSearchParams(window.location.search);

    let clientCode = urlSearchParams.get("clientCode");
    let lang = urlSearchParams.get("lang");
    let sessionKey = urlSearchParams.get("sessionKey");

    let origin = window.location.origin.trim();
    let target = `${origin}/?clientCode={CLIENTCODE}`;
    target = encodeURIComponent(target);

    const loginLink = `https://login.erply.com/?target=${target}&clientCode=${clientCode}`;

    setLink(loginLink);

    if (clientCode) {
      const requestData = {
        clientCode,
        sessionKey: sessionKey ? sessionKey : "",
        lang: lang ? lang : "eng",
        isIframe: false,
      };
      getCodes(requestData);

      getActiveLanguages(requestData.sessionKey, requestData.clientCode);

      getConfParameters(requestData.sessionKey, requestData.clientCode);

      fetchEmployees(requestData);

      fetchStoreRegions(requestData);

      fetchWarehouses(requestData);

      fetchPointOfSales(requestData);

      fetchBrands(requestData);

      fetchSuppliers(requestData);

      fetchCurrency(requestData);

      requestERPLYData({
        clientCode,
        request: "getServiceEndpoints",
      }).then((res) => {
        const baseData = res;
        if (res.data.status.responseStatus === "ok") {
          const userData = {
            baseUrl: baseData.data.records[0].reports.url,
            clientCode,
            sessionKey: sessionKey ? sessionKey : "",
            pimBaseUrl: baseData.data.records[0].pim.url,
            crmBaseUrl: baseData.data.records[0].crm.url,
          };

          Server.init(userData);

          fetchCategories(userData);

          fetchGroups(userData);

          fetchCustomers(userData);

          getCustomerGroups(userData.crmBaseUrl, {
            clientCode: userData.clientCode,
            sessionKey: userData.sessionKey,
          });

          const sessionRequest = {
            request: "getSessionKeyUser",
            clientCode,
          };

          if (sessionKey) {
            sessionRequest.sessionKey = sessionKey;
          }

          requestERPLYData(sessionRequest).then((res) => {
            const result = res;
            if (result.data.status.responseStatus === "ok") {
              getCAFAConfig(
                baseData.data.records[0].cafa.url,
                baseData.data.records[0]["account-admin"].url,
                {
                  sessionKey: sessionKey ? sessionKey : "",
                  clientCode,
                },
                result.data.records[0].userID
              );
              setUser({
                baseUrl: baseData.data.records[0].reports.url,
                sessionKey: sessionKey ? sessionKey : "",
                clientCode,
                lang: lang ? lang : "eng",
                user: result.data.records[0].userName,
                employeeID: result.data.records[0].employeeID,
                isIframe: false,
              });
              setLoading(false);
            } else {
              setUser({
                baseUrl: baseData.data.records[0].reports.url,
                sessionKey: sessionKey ? sessionKey : "",
                clientCode,
                lang: lang ? lang : "eng",
                user: "User",
                isIframe: false,
                employeeID: 0,
              });
              setLoading(false);
            }
          });
        } else {
          setLoading(false);
          notify(`Error performing operation`, "error", 3000);
        }
      });
    } else {
      window.parent.postMessage("SESSION_KEY", "*");
      window.addEventListener(
        "message",
        (message) => {
          if (
            message.origin.includes("erply.com") &&
            message.data.sender === "erply" &&
            message.data.apiSessionKey
          ) {
            const requestData = {
              clientCode: message.data.customerCode,
              sessionKey: message.data.apiSessionKey,
              lang: message.data.page_lang,
              isIframe: true,
            };

            getCodes(requestData);
            getActiveLanguages(requestData.sessionKey, requestData.clientCode);
            getConfParameters(requestData.sessionKey, requestData.clientCode);

            fetchEmployees(requestData);

            fetchStoreRegions(requestData);

            fetchBrands(requestData);

            fetchSuppliers(requestData);

            fetchWarehouses(requestData);

            fetchPointOfSales(requestData);

            fetchCurrency(requestData);

            requestERPLYData({
              clientCode: message.data.customerCode,
              request: "getServiceEndpoints",
            }).then((res) => {
              const baseData = res;

              if (res.data.status.responseStatus === "ok") {
                const userData = {
                  baseUrl: baseData.data.records[0].reports.url,
                  sessionKey: message.data.apiSessionKey,
                  clientCode: message.data.customerCode,
                  pimBaseUrl: baseData.data.records[0].pim.url,
                  crmBaseUrl: baseData.data.records[0].crm.url,
                };
                Server.init(userData);
                fetchCategories(userData);
                fetchGroups(userData);
                fetchCustomers(userData);
                getCustomerGroups(userData.crmBaseUrl, {
                  clientCode: userData.clientCode,
                  sessionKey: userData.sessionKey,
                });
                requestERPLYData({
                  request: "getSessionKeyUser",
                  sessionKey: message.data.apiSessionKey,
                  clientCode: message.data.customerCode,
                }).then((res) => {
                  const result = res;
                  if (result.data.status.responseStatus === "ok") {
                    getCAFAConfig(
                      baseData.data.records[0].cafa.url,
                      baseData.data.records[0]["account-admin"].url,
                      {
                        sessionKey: message.data.apiSessionKey,
                        clientCode: message.data.customerCode,
                      },
                      result.data.records[0].userID
                    );
                    setUser({
                      baseUrl: baseData.data.records[0].reports.url,
                      sessionKey: message.data.apiSessionKey,
                      clientCode: message.data.customerCode,
                      lang: message.data.page_lang,
                      user: result.data.records[0].userName,
                      employeeID: result.data.records[0].employeeID,
                      isIframe: true,
                    });
                    setLoading(false);
                  } else {
                    setUser({
                      baseUrl: baseData.data.records[0].reports.url,
                      sessionKey: message.data.apiSessionKey,
                      clientCode: message.data.customerCode,
                      lang: message.data.page_lang,
                      user: "User",
                      employeeID: 0,
                      isIframe: true,
                    });
                    setLoading(false);
                  }
                });
              } else {
                setLoading(false);
                notify(`Error performing operation`, "error", 3000);
              }
            });
          } else {
            setLoading(false);
          }
        },
        false
      );
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadProgressBar();
  }, []);

  const getActiveLanguages = async (sessionKey, clientCode) => {
    const request = {
      request: "getActiveLanguages",
      clientCode,
      sendContentType: 1,
    };

    if (sessionKey) {
      request.sessionKey = sessionKey;
    }
    try {
      const { data } = await requestERPLYData(request);
      if (data.status.responseStatus === "ok") {
        setActiveLanguages(data.records);
      }
    } catch (error) {}
  };

  const getConfParameters = async (sessionKey, clientCode) => {
    const request = {
      request: "getConfParameters",
      clientCode,
      sendContentType: 1,
    };

    if (sessionKey) {
      request.sessionKey = sessionKey;
    }

    try {
      const { data } = await requestERPLYData(request);
      if (data.status.responseStatus === "ok") {
        setConfigParams(data.records[0]);
      }
    } catch (error) {}
  };

  const getAllWarehousesNew = async (
    baseUrl,
    keys,
    orderBy,
    orderDirection
  ) => {
    try {
      const {
        data: {
          data: { warehouses },
        },
      } = await Server.getAllWarehousesNew(
        baseUrl,
        keys,
        orderBy,
        orderDirection
      );
      setOrderedWarehouses(warehouses);
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify("Error getting warehouses, please try again", "error", 3000);
        }
      } else {
        notify("Error getting warehouses, please try again", "error", 3000);
      }
    }
  };

  const getCAFAConfig = async (cafaUrl, accAdminUrl, keys, level_id) => {
    try {
      const { data } = await Server.getCAFAConfig(cafaUrl, keys, level_id);
      if (data && data.length) {
        const orderBy = data[0].value?.locationselectorder;
        const orderDirection = data[0].value?.locationselectdirection;
        getAllWarehousesNew(accAdminUrl, keys, orderBy, orderDirection);
      } else {
        getAllWarehousesNew(accAdminUrl, keys);
      }
    } catch (error) {
      getAllWarehousesNew(accAdminUrl, keys);
    }
  };

  const getCustomerGroups = async (baseUrl, keys) => {
    try {
      const { data } = await Server.getCustomerGroups(baseUrl, keys);
      setCustomerGroups(data);
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify(
            "Error getting customer groups, please try again",
            "error",
            3000
          );
        }
      } else {
        notify(
          "Error getting customer groups, please try again",
          "error",
          3000
        );
      }
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        loginUrl,
        activeLanguages,
        configParams,
        orderedWarehouses,
        customerGroups,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
